/*
 * <marquee-scroller>
 * Endless horizontal ticker. The `[data-marquee-list]` element holds the items once; this element
 * clones it until the `[data-marquee-track]` covers the viewport plus one extra copy, then a CSS
 * animation (see the marquee section) slides the track left by exactly one list width and loops, so
 * the seam is never visible. Clones are `aria-hidden` and `inert`, so links are only reachable once.
 *
 * Attributes:
 *   data-speed="40"        scroll speed in px per second, the animation duration follows the list width
 *   data-pause-on-hover    also pauses while hovered
 *
 * The clones and the duration are recalculated whenever the list or the element is resized (e.g.
 * once the fonts load). It plays right away and the `[data-marquee-toggle]` button pauses/resumes it.
 * While playing it also pauses when an item is focused or when one of its blocks is selected in the theme
 * editor. With `prefers-reduced-motion` it starts paused.
 *
 * State, for styling: `data-ready` once measured, `data-paused` while not moving.
 */
if (!customElements.get("marquee-scroller")) {
  class MarqueeScroller extends HTMLElement {
    constructor() {
      super();
      this.selectors = {
        track: "[data-marquee-track]",
        list: "[data-marquee-list]",
        toggle: "[data-marquee-toggle]",
      };
    }

    connectedCallback() {
      this.track = this.querySelector(this.selectors.track);
      this.list = this.querySelector(this.selectors.list);
      if (!this.track || !this.list) return;

      this.toggleButton = this.querySelector(this.selectors.toggle);
      this.speed = parseFloat(this.dataset.speed) || 40;
      this.clones = [];
      this.pauseReasons = new Set();
      this.reducedMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)",
      );
      this.isPlaying = !this.reducedMotion.matches;
      this.abortController = new AbortController();
      const { signal } = this.abortController;

      this.toggleButton?.addEventListener(
        "click",
        () => this.setPlaying(!this.isPlaying),
        { signal },
      );

      const pauseOn = (start, end, reason) => {
        this.addEventListener(start, () => this.pause(reason), { signal });
        this.addEventListener(end, () => this.resume(reason), { signal });
      };

      if (this.hasAttribute("data-pause-on-hover"))
        pauseOn("pointerenter", "pointerleave", "hover");
      pauseOn("shopify:block:select", "shopify:block:deselect", "editor");

      // Only focus on the items pauses (so their links stay reachable), not focus on the toggle button
      this.track.addEventListener("focusin", () => this.pause("focus"), {
        signal,
      });
      this.track.addEventListener(
        "focusout",
        (event) => {
          if (!this.track.contains(event.relatedTarget)) this.resume("focus");
        },
        { signal },
      );

      this.resizeObserver = new ResizeObserver(() => this.refresh());
      this.resizeObserver.observe(this);
      this.resizeObserver.observe(this.list);

      this.setPlaying(this.isPlaying);
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.resizeObserver?.disconnect();
    }

    /** Fills the track with enough copies of the list and syncs the animation to the list width. */
    refresh() {
      const listWidth = this.list.getBoundingClientRect().width;
      if (!listWidth) return;

      // One copy more than fits on screen, so the track never runs out while it slides one list width
      const copies = Math.ceil(this.clientWidth / listWidth) + 1;
      this.renderClones(copies - 1);

      this.track.style.setProperty("--marquee-distance", `${listWidth}px`);
      this.track.style.setProperty(
        "--marquee-duration",
        `${listWidth / this.speed}s`,
      );
      this.setAttribute("data-ready", "");
    }

    renderClones(count) {
      if (count === this.clones.length) return;

      while (this.clones.length > count) this.clones.pop().remove();
      while (this.clones.length < count) {
        const clone = this.list.cloneNode(true);
        clone.removeAttribute("data-marquee-list");
        clone.setAttribute("aria-hidden", "true");
        clone.inert = true;
        // The theme editor should only ever pick the original blocks
        clone
          .querySelectorAll("[data-shopify-editor-block]")
          .forEach((element) =>
            element.removeAttribute("data-shopify-editor-block"),
          );

        this.track.append(clone);
        this.clones.push(clone);
      }
    }

    setPlaying(isPlaying) {
      this.isPlaying = isPlaying;
      this.update();

      if (!this.toggleButton) return;
      const { labelPlay, labelPause } = this.toggleButton.dataset;
      this.toggleButton.setAttribute(
        "aria-label",
        isPlaying ? labelPause : labelPlay,
      );
    }

    pause(reason) {
      this.pauseReasons.add(reason);
      this.update();
    }

    resume(reason) {
      this.pauseReasons.delete(reason);
      this.update();
    }

    update() {
      this.toggleAttribute(
        "data-paused",
        !this.isPlaying || this.pauseReasons.size > 0,
      );
      this.toggleAttribute("data-playing", this.isPlaying);
    }
  }

  customElements.define("marquee-scroller", MarqueeScroller);
}
